import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";import { BellRing, FileText, ThumbsUp, Inbox, Truck } from "lucide-react";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "accepted", label: "Quote Accepted" },
  { key: "portal", label: "Portal Requests" },
  { key: "delivered", label: "Deliveries" },
  { key: "pod", label: "POD Reminders" },
];

const KIND_META = {
  portal_quote_accepted: { icon: ThumbsUp, tone: "bg-emerald-50 border-emerald-200 text-emerald-800", chip: "Quote Accepted" },
  portal_repeat_order: { icon: Inbox, tone: "bg-sky-50 border-sky-200 text-sky-800", chip: "Repeat Order" },
  portal_po_upload: { icon: Inbox, tone: "bg-sky-50 border-sky-200 text-sky-800", chip: "PO Upload" },
  portal_enquiry: { icon: Inbox, tone: "bg-sky-50 border-sky-200 text-sky-800", chip: "Enquiry" },
  portal_support: { icon: Inbox, tone: "bg-violet-50 border-violet-200 text-violet-800", chip: "Support" },
  order_delivered: { icon: Truck, tone: "bg-emerald-50 border-emerald-200 text-emerald-800", chip: "Auto-Delivered" },
  pod_reminder: { icon: Truck, tone: "bg-amber-50 border-amber-200 text-amber-800", chip: "Upload POD" },
};

const REF_LINKS = {
  quotation: "/quotations", dispatch: "/dispatch", lead: "/leads",
  sales_order: "/orders", support_ticket: "/support-tickets", customer: "/customers",
};

export default function Notifications() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/notifications/history?kind=${filter}&limit=150`)
      .then((r) => setList(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [filter]);

  return (
    <div className="space-y-4" data-testid="notifications-page">
      <PageHeader title="Notification History" subtitle="Every alert from the portal and auto-tracker — nothing gets lost" />
      <div className="flex flex-wrap items-center gap-1.5" data-testid="notif-filters">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)}
                  className={`text-xs font-semibold rounded-full px-3.5 py-1.5 border transition-colors duration-200 ${
                    filter === f.key ? "bg-[#3B82F6] text-white border-[#3B82F6]" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}
                  data-testid={`notif-filter-${f.key}`}>{f.label}</button>
        ))}
      </div>

      <div className="space-y-2">
        {list.map((a) => {
          const meta = KIND_META[a.kind] || { icon: BellRing, tone: "bg-slate-50 border-slate-200 text-slate-700", chip: a.kind };
          const Icon = meta.icon;
          const link = REF_LINKS[a.ref_type];
          const body = (
            <div className={`flex items-start gap-3 border rounded-lg px-4 py-3 bg-white hover:bg-slate-50/70 transition-colors duration-200`}>
              <span className={`w-8 h-8 rounded-md border flex items-center justify-center shrink-0 ${meta.tone}`}><Icon size={15} /></span>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-[10px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 border ${meta.tone}`}>{meta.chip}</span>
                  <span className="text-[11px] text-slate-400">{fmtDate(a.created_at)}{a.user_email ? ` · ${a.user_name || a.user_email}` : ""}</span>
                </div>
                <p className="text-sm text-slate-700 mt-1">{a.text}</p>
              </div>
            </div>
          );
          return link
            ? <Link key={a.id} to={link} className="block" data-testid={`notif-item-${a.id}`}>{body}</Link>
            : <div key={a.id} data-testid={`notif-item-${a.id}`}>{body}</div>;
        })}
      </div>
      {!loading && list.length === 0 && (
        <p className="text-sm text-slate-400 py-10 text-center bg-white border border-slate-200 rounded-lg" data-testid="notif-empty">
          No notifications in this category yet.
        </p>
      )}
    </div>
  );
}
